import React, { useState } from "react"
import PropTypes from "prop-types"
import HelpText from "../form/HelpText"
import { useForm } from "react-hook-form"
import { showPostPopup } from "../../libs/function"
import { HiCheck } from "react-icons/hi"
import { XMarkIcon } from "@heroicons/react/20/solid"
import { StaticImage } from "gatsby-plugin-image"
import { navigate } from "gatsby"
import getDate from "../../libs/getDate"
import CustomColorRing from "../form/ColorRing"
import { edmCheck, sendToGoogleSheet } from "../../libs/function"
import { SendToGoogleSheetId } from "../../libs/getSheetId"

const PostPopupModal = ({ setIsModal }) => {
  const [isPending, setIsPending] = useState(false)
  const [isHelpText, setIsHelpText] = useState("")

  const curUrl = typeof window !== "undefined" ? window.location.pathname : null

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm()

  const countCloseTimes = () => {
    let popupCloseCounter = localStorage.getItem("popup_close_counter")
    if (popupCloseCounter === null) {
      popupCloseCounter = 0
    }
    popupCloseCounter++

    localStorage.setItem("popup_close_counter", popupCloseCounter)

    // 如果已經關閉兩次，紀錄此刻時間
    if (popupCloseCounter === 2) {
      const curTimestamp = new Date().getTime()
      localStorage.setItem("popup_timestamp", curTimestamp)
    }
  }

  const onSubmit = async data => {
    try {
      const { name, email } = data
      const currentTime = getDate()

      await setIsPending(true)

      const body = JSON.stringify({
        name,
        email,
        dayOfCycle: 0,
        campaign: {
          campaignId: "qvwPF",
        },
      })
      const formData = {
        rows: {
          name: name,
          email: email,
          type: "pop-up",
          slug: curUrl,
          date: currentTime,
        },
        docId: SendToGoogleSheetId.default,
        sheetId: "281173528", // 工作表名稱：EDM訂閱名單
      }

      // 透過edm.js檢查是否重複或格式有誤
      const result = await edmCheck(body)

      // 若回傳status===200，則寫入google-sheet
      if (result.status === 200) {
        await sendToGoogleSheet(formData)
        await setIsPending(false)
        await reset()
        setIsHelpText("訂閱成功，請至信箱查看是否有收到 Welly 的第一封信")
        localStorage.setItem("isSubscribe", "true")
        await navigate(`${curUrl}?edmSuccess=true`)
      } else if (result.status === 400) {
        await setIsPending(false)
        setIsHelpText("信箱格式錯誤")
      } else if (result.status === 409) {
        await setIsPending(false)
        setIsHelpText("您的 email 已經訂閱，若有問題請加 LINE 詢問")
        localStorage.setItem("isSubscribe", "true")
      } else {
        await setIsPending(false)
        setIsHelpText("發生錯誤! 請加 LINE 聯繫我們")
      }
    } catch (e) {
      await setIsPending(false)
      alert("發生不可預期的錯誤! 請加line聯繫我們")
    }
  }

  const learningResources = [
    "用 EDM 開啟學習導覽，每週一篇、循序漸進",
    "每一季定期推出的懶人包電子書",
    "Welly 追蹤的 SEO & 行銷新知與趨勢",
  ]
  const learningResourcesBlock = learningResources.map((resource, index) => {
    return (
      <div key={index} className="flex">
        <HiCheck className="text-yellow-400 mr-1 w-6 h-6 sm:w-[28px] sm:h-[28px]" />
        <p className="text-sm sm:text-base">{resource}</p>
      </div>
    )
  })

  return (
    <>
      <div
        className="fixed inset-0 z-20 bg-black opacity-50"
        onClick={() => {
          setTimeout(() => {
            setIsModal(false)
            countCloseTimes()

            // 關閉彈窗後，閒置30秒再次開啟彈窗
            showPostPopup(30, setIsModal)
          }, 0)
        }}
      />
      <div className="popupModal fixed top-1/2 -translate-y-1/2 z-20 border-t-4 border-t-yellow-300 sm:max-w-3xl sm:border-t-0">
        <div className="flex bg-white">
          <div className="hidden sm:inline-block sm:w-80 flex-shrink-0">
            <StaticImage
              src="../../images/img_ArticlePopup_ebook_D.jpg"
              alt="img_ArticlePopup_ebook"
              className="h-full object-cover"
              placeholder="blurred"
              quality={100}
            />
          </div>
          <div className="p-6">
            <div className="w-full flex justify-end items-center">
              <button
                onClick={() => {
                  setTimeout(() => {
                    setIsModal(false)
                    countCloseTimes()

                    // 關閉彈窗後，閒置30秒再次開啟彈窗
                    showPostPopup(30, setIsModal)
                  }, 0)
                }}
              >
                <XMarkIcon className="block h-6 w-8 translate-x-1.5 text-gray-600" />
              </button>
            </div>
            <p className="mt-2 mb-3 text-2xl !leading-tight text-center font-bold text-gray-900 sm:text-3xl sm:text-left">
              想定期收到 SEO 教學懶人包與行銷新知嗎？
            </p>
            {/* 訂閱區塊 */}
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="grid grid-cols-1 col-span-2 gap-3 sm:pt-0 sm:justify-self-stretch"
              id="collapseTop"
            >
              <input
                {...register("name", {
                  required: true,
                  minLength: 2,
                  maxLength: 128,
                })}
                placeholder="希望 Welly 如何稱呼您？"
                className={`bg-gray-100 w-full h-10 pl-4 text-lg outline-none tracking-wide text-gray-900 border-b border-b-gray-900 placeholder:text-gray-400 focus:border-b-2 ${
                  errors["name"] ? "border-b-yellow-500" : ""
                }`}
              />
              {errors["name"] && <HelpText text="*此為必填項目(兩字以上)" />}
              <input
                {...register("email", {
                  required: true,
                })}
                placeholder="您常用的 Email"
                className={`bg-gray-100 w-full h-10 pl-4 text-lg outline-none tracking-wide text-gray-900 border-b border-b-gray-900 placeholder:text-gray-400 focus:border-b-2 ${
                  errors["email"] ? "border-b-yellow-500" : ""
                }`}
              />
              {errors["email"] && <HelpText text="*此為必填項目" />}
              {isPending ? (
                <CustomColorRing />
              ) : (
                <button
                  type="submit"
                  className="block mx-auto py-2 px-3 text-sm text-center font-medium text-gray-900 bg-yellow-300 transition ease-in-out duration-150 hover:bg-yellow-400 active:bg-yellow-500 sm:px-4 sm:mr-auto sm:ml-0"
                >
                  訂閱電子報
                </button>
              )}
            </form>
            <p className="mt-3 text-center font-bold text-yellow-500 sm:leading-normal sm:text-left">
              {isHelpText}
            </p>
            <p className="mt-6 mb-3 font-bold text-gray-900 truncate sm:text-lg sm:leading-normal">
              訂閱完成你將會獲得...
            </p>
            <div className="flex flex-col gap-2">{learningResourcesBlock}</div>
          </div>
        </div>
      </div>
    </>
  )
}

PostPopupModal.propTypes = {
  setIsModal: PropTypes.func,
}

export default PostPopupModal
